import type {
  NamedItemGetPayload,
  NamedItemSelect,
  UserGetPayload,
  UserSelect,
  SpaceUser,
} from '@erikdakoda/database/types';
import type { updateUser, authClient } from './client/betterAuthClient';
import type { BetterFetchError, SessionQueryParams } from 'better-auth/client';

export type UpdateUser = typeof updateUser;
export type Session = typeof authClient.$Infer.Session;
export type AuthUser = typeof authClient.$Infer.Session.user & {
  spaces: Pick<SpaceUser, 'id' | 'createdAt' | 'updatedAt' | 'spaceId' | 'userId' | 'role'>[];
};

export type CurrentUserContext = {
  user: AuthUser | null;
  expires: string;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  isUserLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  error: BetterFetchError | null;
  refetchCurrentUser: (queryParams?: { query?: SessionQueryParams }) => void | Promise<void>;
  updateCurrentUser: UpdateUser;
};

// https://www.better-auth.com/docs/concepts/users-accounts

export const UserForIdentificationSelect = {
  id: true,
  activeSpaceId: true,
  createdAt: true,
  updatedAt: true,
  email: true,
  emailVerified: true,
  givenName: true,
  familyName: true,
  nickName: true,
  image: true,
  role: true,
  privacy: true,
  disableAdmin: true,
} satisfies UserSelect;

export type UserForIdentification = UserGetPayload<{
  select: typeof UserForIdentificationSelect;
}>;

export const UserForAuthSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  email: true,
  emailVerified: true,
  phoneNumber: true,
  phoneNumberVerified: true,
  givenName: true,
  familyName: true,
  image: true,
  role: true,
  privacy: true,
  activeSpaceId: true,
  repairedAt: true,
  nickName: true,
  dateOfBirth: true,
  gender: true,
  relationship: true,
  language: true,
  timezone: true,
  firstDayOfWeek: true,
  citizenship: true,
  country: true,
  state: true,
  city: true,
  coachId: true,
  onboardingTrack: true,
  verbosity: true,
  journeyStepId: true,
  lifeDomainIds: true,
  disableAdmin: true,
  spaces: {
    select: {
      id: true,
      createdAt: true,
      updatedAt: true,
      spaceId: true,
      space: true,
      role: true,
    },
  },
} satisfies UserSelect;

export type UserForAuth = UserGetPayload<{
  select: typeof UserForAuthSelect;
}>;

export const nonUpdatableSessionFields = [
  'id',
  'createdAt',
  'updatedAt',
  'email',
  'emailVerified',
  'phoneNumber',
  'phoneNumberVerified',
  'role',
  'repairedAt',
  'spaces',
];

export const updatableSessionFields = Object.keys(UserForAuthSelect).filter(
  (key) => !nonUpdatableSessionFields.includes(key),
);

export const NamedItemForOwnerSelect = {
  ownerId: true,
  spaceId: true,
  private: true,
  publishedAt: true,
} satisfies NamedItemSelect;

export type NamedItemForOwner = NamedItemGetPayload<{
  select: typeof NamedItemForOwnerSelect;
}>;
